'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import {
  convertUnit,
  createUnitConversionJson,
  getUnitCategory,
  type UnitCategoryKey,
  type UnitConversionResult,
} from '@/lib/browser-unit-tools';

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserUnitConverter({
  variant,
  config,
}: {
  variant: UnitCategoryKey;
  // Pair landing pages (/tools/cm-to-inches) preset the units and sample value.
  config?: {
    defaultUnitFrom?: string;
    defaultUnitTo?: string;
    defaultUnitValue?: number;
    toolSlug?: string;
  };
}) {
  const category = useMemo(() => getUnitCategory(variant), [variant]);
  const [value, setValue] = useState(String(config?.defaultUnitValue ?? category.sampleValue));
  const [fromId, setFromId] = useState(config?.defaultUnitFrom ?? category.defaultFrom);
  const [toId, setToId] = useState(config?.defaultUnitTo ?? category.defaultTo);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  // Counts the first real action (typing, changing units, swapping, copying), never
  // the preset render, which would log every page view as a use.
  const reported = useRef(false);
  function reportUse() {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? `${variant}-converter`, '').catch(() => undefined);
  }

  let result: UnitConversionResult | null = null;
  let errorMessage: string | null = null;
  try {
    result = convertUnit(category, value, fromId, toId);
  } catch (error) {
    errorMessage = error instanceof Error ? error.message : 'Enter a valid number to convert.';
  }

  function swapUnits() {
    reportUse();
    setCopyMessage(null);
    setFromId(toId);
    setToId(fromId);
  }

  async function handleCopy(text: string, label: string) {
    reportUse();
    try {
      await copyText(text);
      setCopyMessage(label);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{category.label}</span>
        <h2 className="tool-live-title">Convert {category.measure} units instantly</h2>
        <p className="tool-live-subtitle">
          Enter a value, pick the units, and the conversion updates live in your browser. Nothing is
          uploaded and no server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Value</span>
              <input
                inputMode="decimal"
                onChange={(event) => {
                  setCopyMessage(null);
                  reportUse();
                  setValue(event.target.value);
                }}
                placeholder={String(category.sampleValue)}
                type="text"
                value={value}
              />
            </label>
            <label className="tool-live-field">
              <span>From</span>
              <select
                onChange={(event) => {
                  setCopyMessage(null);
                  reportUse();
                  setFromId(event.target.value);
                }}
                value={fromId}
              >
                {category.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>To</span>
              <select
                onChange={(event) => {
                  setCopyMessage(null);
                  reportUse();
                  setToId(event.target.value);
                }}
                value={toId}
              >
                {category.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={swapUnits} type="button">
              Swap units
            </button>
            {result ? (
              <button
                className="button button-secondary"
                onClick={() => handleCopy(result.formatted, 'Result copied to clipboard.')}
                type="button"
              >
                Copy result
              </button>
            ) : null}
            {result ? (
              <button
                className="button button-secondary"
                onClick={() =>
                  handleCopy(createUnitConversionJson(result), 'Conversion JSON copied.')
                }
                type="button"
              >
                Copy JSON
              </button>
            ) : null}
          </div>

          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {result ? (
            <>
              <div className="tool-live-stats">
                <p className="tool-live-title">{result.expression}</p>
                <p className="tool-live-subtitle">
                  {result.formatted} {result.toSymbol}
                </p>
              </div>

              <div className="tool-html-meta-grid">
                {result.table.map((row) => (
                  <div className="tool-live-field" key={row.id}>
                    <span>
                      {row.name} ({row.symbol})
                    </span>
                    <input readOnly type="text" value={row.formatted} />
                  </div>
                ))}
              </div>

              <p className="tool-live-panel-note">
                All conversions run locally with double-precision math. Values are rounded for
                display only.
              </p>
            </>
          ) : (
            <p className="tool-live-panel-note">Enter a number to see the conversion.</p>
          )}
        </section>
      </div>
    </div>
  );
}
